import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Server, CheckCircle, XCircle, MessageSquare, 
  CreditCard, Loader2, RefreshCw, Settings, Shield, Smartphone,
  Coins, ToggleLeft, ToggleRight, Users, TrendingUp
} from 'lucide-react';
import { adminAPI } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [matchTradeStatus, setMatchTradeStatus] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [tokenStats, setTokenStats] = useState(null);
  const [togglingSeed, setTogglingSeed] = useState(false);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [mtRes, smsRes, tokenRes] = await Promise.all([
        adminAPI.getMatchTradeStatus(),
        adminAPI.getSMSStatus(),
        fetch(`${API_URL}/api/token/admin/overview`, { headers: { 'Authorization': `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null).catch(() => null)
      ]);
      setMatchTradeStatus(mtRes.data);
      setSmsStatus(smsRes.data);
      setTokenStats(tokenRes);
    } catch (error) {
      toast.error('Failed to fetch integration statuses');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSeedRound = async () => {
    setTogglingSeed(true);
    try {
      const token = localStorage.getItem('token');
      const newState = !tokenStats?.config?.seed_round_open;
      const res = await fetch(`${API_URL}/api/token/admin/seed-round`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_open: newState })
      });
      if (!res.ok) throw new Error('Failed to toggle');
      const data = await res.json();
      toast.success(data.message);
      fetchStatuses();
    } catch (error) {
      toast.error('Failed to toggle seed round');
    } finally {
      setTogglingSeed(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setSendingTest(true);
    try {
      await adminAPI.testSMS(testPhone);
      toast.success('Test SMS sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="admin-settings">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/admin')}
            className="mb-4 border-white/10 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Admin Dashboard
          </Button>
          
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="w-8 h-8 text-cyan-400" />
            Integration <span className="text-cyan-400">Settings</span>
          </h1>
          <p className="text-gray-400 mt-1">Configure broker and notification integrations</p>
        </motion.div>

        {/* Integration Cards */}
        <div className="space-y-6">
          {/* Match-Trade Integration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Server className="w-6 h-6 text-blue-400" />
                    Match-Trade Broker Integration
                  </div>
                  <Badge className={matchTradeStatus?.connected 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }>
                    {matchTradeStatus?.connected ? (
                      <><CheckCircle className="w-3 h-3 mr-1" /> Connected</>
                    ) : (
                      <><XCircle className="w-3 h-3 mr-1" /> Not Connected</>
                    )}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-400 text-sm">
                  {matchTradeStatus?.message}
                </p>
                
                {!matchTradeStatus?.configured && (
                  <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-yellow-400 text-sm font-medium mb-2">Configuration Required</p>
                    <p className="text-gray-400 text-sm">
                      Add the following to your backend environment (.env):
                    </p>
                    <pre className="mt-2 p-3 bg-black/50 rounded text-xs text-gray-300 overflow-x-auto">
{`MATCHTRADE_BASE_URL=https://your-instance.match-trade.com
MATCHTRADE_API_KEY=your-api-key-here`}
                    </pre>
                  </div>
                )}

                {matchTradeStatus?.connected && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-gray-500 text-sm">Offers Available</p>
                      <p className="text-white font-mono text-lg">{matchTradeStatus?.offers_count || 0}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-gray-500 text-sm">Status</p>
                      <p className="text-green-400 font-medium">Active</p>
                    </div>
                  </div>
                )}

                <Button 
                  variant="outline" 
                  onClick={fetchStatuses}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Status
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* SMS (Twilio) Integration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-6 h-6 text-purple-400" />
                    SMS Notifications (Twilio)
                  </div>
                  <Badge className={smsStatus?.available 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }>
                    {smsStatus?.available ? (
                      <><CheckCircle className="w-3 h-3 mr-1" /> Active</>
                    ) : (
                      <><XCircle className="w-3 h-3 mr-1" /> Not Configured</>
                    )}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-400 text-sm">
                  {smsStatus?.message}
                </p>
                
                {!smsStatus?.configured && (
                  <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-yellow-400 text-sm font-medium mb-2">Configuration Required</p>
                    <p className="text-gray-400 text-sm">
                      Add the following to your backend environment (.env):
                    </p>
                    <pre className="mt-2 p-3 bg-black/50 rounded text-xs text-gray-300 overflow-x-auto">
{`TWILIO_ACCOUNT_SID=your-account-sid
TWILIO_AUTH_TOKEN=your-auth-token
TWILIO_PHONE_NUMBER=+1234567890`}
                    </pre>
                    <p className="text-gray-500 text-xs mt-2">
                      Get credentials from: <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">console.twilio.com</a>
                    </p>
                  </div>
                )}

                {smsStatus?.available && (
                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                      <p className="text-green-400 text-sm">SMS service is active and ready to send notifications</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-gray-400">Send Test SMS</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="+1234567890"
                          value={testPhone}
                          onChange={(e) => setTestPhone(e.target.value)}
                          className="bg-white/5 border-white/10 text-white"
                        />
                        <Button 
                          onClick={handleTestSMS}
                          disabled={sendingTest}
                          className="bg-purple-500 hover:bg-purple-600"
                        >
                          {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Stripe Integration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-6 h-6 text-green-400" />
                    Stripe Payments
                  </div>
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                    Test Mode
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-400 text-sm">
                  Stripe is configured in TEST mode. Payments will not process real money.
                </p>
                
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <p className="text-yellow-400 text-sm font-medium mb-2">Go Live Checklist</p>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Replace test key with live key in .env</li>
                    <li>• Update webhook endpoint in Stripe Dashboard</li>
                    <li>• Enable live mode in Stripe Dashboard</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Email Integration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-6 h-6 text-cyan-400" />
                    Email Notifications (Resend)
                  </div>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    <CheckCircle className="w-3 h-3 mr-1" /> Active
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Email notifications are active and working via Resend.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* KBTCOIN Token Management */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-[hsl(220,20%,7%)] border-yellow-500/20" data-testid="kbtcoin-admin-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Coins className="w-6 h-6 text-yellow-400" />
                    KBTCOIN Token Management
                  </div>
                  <Badge className={tokenStats?.config?.seed_round_open
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }>
                    {tokenStats?.config?.seed_round_open ? 'Seed Round Open' : 'Seed Round Closed'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {tokenStats ? (
                  <>
                    {/* Token Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-white/5">
                        <p className="text-gray-500 text-xs uppercase tracking-wider">Total Supply</p>
                        <p className="text-white font-mono text-lg">{tokenStats.config?.total_supply?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5">
                        <p className="text-gray-500 text-xs uppercase tracking-wider">In Circulation</p>
                        <p className="text-yellow-400 font-mono text-lg">{tokenStats.config?.tokens_in_circulation?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5">
                        <p className="text-gray-500 text-xs uppercase tracking-wider">Seed Round Sold</p>
                        <p className="text-cyan-400 font-mono text-lg">{tokenStats.config?.seed_round_sold?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5">
                        <p className="text-gray-500 text-xs uppercase tracking-wider">Token Price</p>
                        <p className="text-green-400 font-mono text-lg">£{tokenStats.config?.token_price_gbp?.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Seed Round Progress */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">Seed Round Progress</span>
                        <span className="text-sm font-mono text-white">
                          {tokenStats.config?.seed_round_sold?.toLocaleString()} / {tokenStats.config?.seed_round_total?.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all"
                          style={{ width: `${((tokenStats.config?.seed_round_sold || 0) / (tokenStats.config?.seed_round_total || 1)) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-white/5 flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-400" />
                        <div>
                          <p className="text-gray-500 text-xs">Holders</p>
                          <p className="text-white font-mono">{tokenStats.metrics?.total_holders || 0}</p>
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <div>
                          <p className="text-gray-500 text-xs">Purchases</p>
                          <p className="text-white font-mono">{tokenStats.metrics?.total_purchases || 0}</p>
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-400" />
                        <div>
                          <p className="text-gray-500 text-xs">Active Stakes</p>
                          <p className="text-white font-mono">{tokenStats.metrics?.active_stakes || 0}</p>
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-yellow-400" />
                        <div>
                          <p className="text-gray-500 text-xs">Revenue</p>
                          <p className="text-white font-mono">£{tokenStats.metrics?.total_revenue_gbp?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Toggle Seed Round Button */}
                    <div className="flex items-center justify-between p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                      <div>
                        <p className="font-medium text-white">Seed Round Control</p>
                        <p className="text-gray-400 text-sm">
                          {tokenStats.config?.seed_round_open 
                            ? 'Seed round is currently open. Users can purchase tokens.' 
                            : 'Seed round is closed. No purchases allowed.'}
                        </p>
                      </div>
                      <Button
                        onClick={handleToggleSeedRound}
                        disabled={togglingSeed}
                        className={tokenStats.config?.seed_round_open
                          ? 'bg-red-500 hover:bg-red-600 text-white'
                          : 'bg-green-500 hover:bg-green-600 text-white'
                        }
                        data-testid="toggle-seed-round-btn"
                      >
                        {togglingSeed ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : tokenStats.config?.seed_round_open ? (
                          <><ToggleRight className="w-4 h-4 mr-2" /> Close Seed Round</>
                        ) : (
                          <><ToggleLeft className="w-4 h-4 mr-2" /> Open Seed Round</>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">Token stats unavailable</p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
